import { useMemo } from 'react';
import { parseISO, subDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Line, LineChart, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';

import { Tooltip } from '@app/components';
import { AdminDashboardTileOutDto } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';

const fmt = (n: number) => n.toLocaleString('nl-NL');

// admin-dashboard-opdracht.md §4.2: "Elke tegel krijgt een sparkline... Twee hoverlagen: over de
// tegel het gemiddelde/hoogste/laagste; over de sparkline zelf de waarde per dag met het
// verschil t.o.v. de dag ervoor." Two separate hover zones (not one overlapping region like the
// mockup's own JS has to special-case) - the aggregate Tooltip wraps everything except the
// sparkline, which has its own per-point recharts Tooltip.
//
// Found live on dev 2026-08-24 (Danny): the sparkline must also follow the selected period, not
// stay fixed at a trailing 14 real days regardless - AdminDashboardFacadeImpl now returns one
// point per day spanning the resolved period (capped, see MAX_SPARKLINE_PERIOD_DAYS), so `to`
// (the tile's own resolved end date, capped at "today" server-side too) is needed here to
// reconstruct each point's real calendar date for the tooltip - can no longer assume the last
// point is always today.
export const DashboardTile: React.FC<{ tile: AdminDashboardTileOutDto; color: string; to: string }> = ({
  tile,
  color,
  to,
}) => {
  const { avg, min, max, points } = useMemo(() => {
    const n = tile.sparkline.length;
    const sum = tile.sparkline.reduce((a, b) => a + b, 0);
    const today = new Date();
    const parsedTo = parseISO(to);
    const lastDay = parsedTo > today ? today : parsedTo;
    const points = tile.sparkline.map((v, i) => ({
      day: subDays(lastDay, n - 1 - i),
      value: v,
      diff: i === 0 ? null : v - tile.sparkline[i - 1],
    }));
    return {
      avg: Math.round(sum / n),
      min: Math.min(...tile.sparkline),
      max: Math.max(...tile.sparkline),
      points,
    };
  }, [tile.sparkline, to]);

  const delta = tile.deltaPercent;
  const trend = delta === undefined || delta === null ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const arrow = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '–';
  const trendColor = trend === 'up' ? 'text-[#217a1b]' : trend === 'down' ? 'text-[#b4433f]' : 'text-gray-400';

  return (
    <div className="min-w-0 rounded-md bg-white p-4 shadow-card">
      <Tooltip
        placement="top"
        title={
          <div className="py-0.5">
            <div className="font-bold">{tile.label}</div>
            <div>
              Nu: <b>{fmt(tile.value)}</b>
            </div>
            <div className="opacity-80">{tile.comparisonLabel}</div>
            <div className="opacity-80">
              Gemiddeld {points.length} {points.length === 1 ? 'dag' : 'dagen'}: {fmt(avg)}
            </div>
            <div className="opacity-60">
              Hoogste: {fmt(max)} · laagste: {fmt(min)}
            </div>
          </div>
        }
      >
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-gray-500">
            {tile.label}
            {tile.running && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-medium text-gray-500">
                loopt nog
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[30px] font-bold leading-none tracking-tight text-gray-800 [font-variant-numeric:tabular-nums]">
            {fmt(tile.value)}
          </div>
          <div className={clsxm('mt-1 inline-flex items-center gap-1 text-[12.5px] font-medium', trendColor)}>
            {arrow} {delta !== undefined && delta !== null ? Math.abs(delta) : 0}%
          </div>
          <div className="mt-0.5 text-[11.5px] text-gray-400">{tile.comparisonLabel}</div>
        </div>
      </Tooltip>

      <div className="mt-2 h-[34px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <RTooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload?.length) {
                  return null;
                }
                const p = payload[0].payload as typeof points[number];
                const isLast = points[points.length - 1] === p;
                return (
                  <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] leading-relaxed text-white">
                    <b>{nl.localize?.day(p.day.getDay(), { width: 'abbreviated' })}</b>{' '}
                    {p.day.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                    {isLast && <span className="opacity-70"> (loopt nog)</span>}
                    <br />
                    {tile.label}: <b>{fmt(p.value)}</b>
                    {p.diff !== null && (
                      <span className="opacity-60">
                        {' '}
                        · dag ervoor: {p.diff >= 0 ? '+' : ''}
                        {fmt(p.diff)}
                      </span>
                    )}
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3.5, fill: color, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
